import { useEffect, useRef, useState, useCallback } from 'react';
import type { 
  ConnectionStatus, 
  PlayerPosition, 
  GameServerMessage 
} from '../types';

interface UseGameServerOptions {
  wsUrl: string;
  peerId: string;
  reconnectInterval?: number;
  pingInterval?: number;
  /** Test mode - use fake position */
  testMode?: boolean;

  /** Display name for test mode */
  displayName?: string;
  /** Huddle01-assigned peer ID (for test mode, use this instead of config peerId) */
  huddlePeerId?: string | null;
}

interface UseGameServerReturn {
  status: ConnectionStatus;
  positions: Record<string, PlayerPosition>;
  error: string | null;
  reconnect: () => void;
  /** Send identify_test message with Huddle01 peerId (call after joining room) */
  sendTestIdentify: (huddlePeerId: string) => void;
}

/**
 * Hook to connect to the Hytale game server WebSocket for position updates.
 * This is called AFTER authentication - the connection is already authenticated.
 */
export function useGameServer({
  wsUrl,
  peerId,
  reconnectInterval = 5000,
  pingInterval = 5000,
  testMode = false,
  displayName = 'TestUser',
  huddlePeerId,
}: UseGameServerOptions): UseGameServerReturn {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [positions, setPositions] = useState<Record<string, PlayerPosition>>({});
  const [error, setError] = useState<string | null>(null);
  const [testIdentifySent, setTestIdentifySent] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Function to send identify_test with Huddle01 peerId
  const sendTestIdentify = useCallback((actualPeerId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[ProxChat] Cannot send identify_test - WebSocket not connected');
      return;
    }
    if (!testMode) {
      console.warn('[ProxChat] sendTestIdentify called but not in test mode');
      return;
    }
    if (testIdentifySent) {
      console.log('[ProxChat] identify_test already sent, skipping');
      return;
    }

    console.log('[ProxChat] Sending identify_test with Huddle01 peerId:', actualPeerId);
    wsRef.current.send(JSON.stringify({
      type: 'identify_test',
      peerId: actualPeerId,
      displayName: displayName,
    }));
    setTestIdentifySent(true);
  }, [testMode, displayName, testIdentifySent]);

  const connect = useCallback(() => {
    if (!wsUrl) {
      setError('No WebSocket URL provided');
      setStatus('error');
      return;
    }

    clearTimers();
    
    if (wsRef.current) {
      wsRef.current.close();
    }

    setStatus('connecting');
    setError(null);
    setTestIdentifySent(false);

    // In test mode, server forces a fixed hard-coded position for test users.
    // No coordinates need to be provided by the client.

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        setError(null);
        console.log('[ProxChat] Connected to game server for position updates');

        // For normal mode, send identify immediately
        // For test mode, DON'T send yet - wait for Huddle01 peerId via sendTestIdentify()
        if (!testMode) {
          ws.send(JSON.stringify({
            type: 'identify',
            peerId: peerId,
          }));
        } else {
          console.log('[ProxChat] Test mode: waiting for Huddle01 peerId before sending identify_test');
        }

        // Start ping interval to keep connection alive
        // NOTE: In test mode we don't have a playerUuid, so `ping` is a no-op server-side.
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: 'ping',
              timestamp: Date.now(),
            }));
          }
        }, pingInterval);
      };

      ws.onmessage = (event) => {
        try {
          const message: GameServerMessage = JSON.parse(event.data);
          
          if (message.type === 'positions') {
            setPositions(message.players);
          }
          // auth_success/auth_error handled by App.tsx before VoiceRoom mounts
          // pong messages are ignored (used for latency tracking if needed)
        } catch (e) {
          console.warn('[ProxChat] Failed to parse message:', e);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setStatus('error');
      };

      ws.onclose = () => {
        setStatus('disconnected');
        clearTimers();
        console.log('[ProxChat] Disconnected from game server');

        // Auto-reconnect
        reconnectTimeoutRef.current = window.setTimeout(() => {
          console.log('[ProxChat] Attempting to reconnect...');
          connect();
        }, reconnectInterval);
      };
    } catch (e) {
      setError(`Failed to connect: ${e}`);
      setStatus('error');
    }
  }, [wsUrl, peerId, pingInterval, reconnectInterval, clearTimers, testMode]);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    connect();

    return () => {
      clearTimers();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, clearTimers]);

  // Auto-send identify_test when huddlePeerId becomes available (for test mode)
  useEffect(() => {
    if (testMode && huddlePeerId && !testIdentifySent && status === 'connected') {
      sendTestIdentify(huddlePeerId);
    }
  }, [testMode, huddlePeerId, testIdentifySent, status, sendTestIdentify]);

  return {
    status,
    positions,
    error,
    reconnect,
    sendTestIdentify,
  };
}
